/**
 * What a cue means, in sound and in the phone's motor. No React, no audio —
 * just the table and the rules, so both are testable without a browser.
 *
 * The sounds themselves live in `cues.mp3`, built by `scripts/sounds.mjs`;
 * `cueSprite.ts` says where each one starts. This file says which button earns
 * one and how hard the phone should buzz when it does.
 */

import { CUE_SPRITE, type CueName } from "./cueSprite";

export type { CueName };
export { CUE_SPRITE };

/**
 * Haptics, in the same `navigator.vibrate` grammar the sweep beeps use:
 * a number is one buzz, an array alternates buzz and pause.
 *
 * These are deliberately an order of magnitude shorter than a notification
 * buzz. The longest run here is 150ms end to end; a WhatsApp message is 400.
 * The motor is being used as a texture under a fingertip, not as an alarm —
 * anything you consciously notice as "the phone vibrated" is too long.
 *
 * The three that use a PATTERN rather than a single pulse are the three where
 * the phone might be at your ear, in a pocket, or face down on a counter:
 * "threw it", something landing, and a failed write. A pattern survives being
 * felt through a jacket; a 20ms tap does not.
 */
export const CUE_HAPTICS: Record<CueName, number | number[]> = {
  press: 8,
  ate: 18,
  tossed: [14, 40, 14],
  still: 10,
  add: [12, 40, 26],
  remove: 22,
  error: [40, 70, 40],
};

/**
 * Which buttons get the plain press tick.
 *
 * A CTA is a control that **commits or advances**. Everything else — Cancel,
 * Back, the nav, a close ✕, a disclosure chevron — is deliberately silent, and
 * that is the whole design: feedback that fires on every tap carries no
 * information, and a phone that buzzes when you press Back is a phone people
 * turn the setting off on. Opt-OUT is right for a ripple, which is only ever
 * confirming that a press registered. Opt-IN is right for a noise.
 *
 * `.ghost` is the secondary button in every pair in this app and is excluded by
 * omission rather than by rule — it is never the committing half.
 */
export const CTA = ".primary, .answer, .fab, .cart-tick, .rowbutton, .scanbtn";

/** Escape hatch for a CTA-classed button that should stay quiet. */
export const OPT_OUT = ".no-cue";

/** What the DOM adapter below reads off a pressed button. */
export interface Pressed {
  /** The button's `data-cue`, if it named one. */
  declared?: string;
  /** Matches {@link CTA} — a control that commits or advances. */
  isCta: boolean;
  disabled: boolean;
  /** Matches {@link OPT_OUT}. */
  optedOut: boolean;
}

/**
 * The cue a press deserves, or null for silence. Pure, so it is the part with
 * the tests — the adapter under it only reads attributes.
 *
 * `data-cue` WINS over the CTA rule, and that is what stops the two layers
 * doubling up: the three answer buttons are `.answer`, so without this they
 * would tick AND chime off one press. Declaring a cue replaces the default; it
 * does not add to it.
 *
 * An unknown `data-cue` is silence rather than a fallback tick. A typo should
 * be noticeable, and a button that names a cue has opted out of the generic
 * one whether or not the name was any good.
 */
export function cueFor({ declared, isCta, disabled, optedOut }: Pressed): CueName | null {
  if (disabled || optedOut) return null;
  if (declared) return declared in CUE_SPRITE ? (declared as CueName) : null;
  return isCta ? "press" : null;
}

/** Reads a pressed element and asks {@link cueFor}. The only DOM in this file. */
export function cueForPress(el: Element | null): CueName | null {
  const host = el?.closest<HTMLElement>("button, [role='button']");
  if (!host) return null;
  return cueFor({
    declared: host.dataset.cue,
    isCta: host.matches(CTA),
    disabled:
      host.hasAttribute("disabled") || host.getAttribute("aria-disabled") === "true",
    optedOut: host.matches(OPT_OUT),
  });
}

/**
 * Where the on/off switch is remembered.
 *
 * Per DEVICE, not per household, and so localStorage rather than Firestore.
 * Sound is a property of where you are — the kitchen tablet may keep it, the
 * phone you check in bed may not — and syncing that across devices would be
 * confidently wrong in the way this repo already has a rule about.
 */
const KEY = "larder.cues";

/**
 * Defaults ON, which is the one genuinely arguable call here.
 *
 * The web's instinct is that noise defaults off, and it is a good instinct for
 * a page you arrived at from a search result. This is an installed app on a
 * phone, and a phone already has a hardware answer to "not now": the silent
 * switch mutes the audio and leaves the haptics, which is exactly the right
 * behaviour and not one we would have thought to build. A default of off would
 * mean approximately nobody ever hears any of this.
 *
 * Nothing here is ever the only signal — every cue accompanies a visible
 * change — so the setting only ever removes decoration.
 */
export function readCuePref(store: Storage | undefined = safeStorage()): boolean {
  try {
    return store?.getItem(KEY) !== "off";
  } catch {
    return true;
  }
}

export function writeCuePref(
  on: boolean,
  store: Storage | undefined = safeStorage(),
): void {
  try {
    store?.setItem(KEY, on ? "on" : "off");
  } catch {
    /* Private mode, or a browser with storage disabled. The setting simply
       does not survive the session, which beats taking the screen down. */
  }
}

function safeStorage(): Storage | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

/**
 * Buzz, if the device has a motor and is willing.
 *
 * Safari on iOS has no `navigator.vibrate` at all and is not going to get one,
 * so on an iPhone this whole layer is the sound alone. That is fine and is why
 * neither half is ever load-bearing on its own.
 */
export function buzz(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* Some browsers throw rather than returning false. Not worth a log line. */
  }
}
